import { Helmet } from 'react-helmet'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import SimpleSortTable, { TableColumn, TableData } from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'

// Verified against backend/app/Models/User.ts, backend/app/Models/AdminInvitation.ts,
// backend/app/Models/SupplyChainInvitation.ts, the access_audit_events migration
// (backend/database/migrations/1790600000070_create_access_audit_events.ts) and
// backend/app/lib/access/assumption/recordAccessEvent.ts. Static data only - never real records.
const columns: TableColumn[] = [
	{ id: 'dataItem', label: 'Data item' },
	{ id: 'recordedWhen', label: 'Recorded when' },
	{ id: 'storedIn', label: 'Stored in' },
	{ id: 'visibleTo', label: 'Visible to' },
	{ id: 'status', label: 'Status' },
]

const rows: TableData[] = [
	{
		_id: 'first-login',
		dataItem: 'First login',
		recordedWhen: "A user's first successful login",
		storedIn: 'users.first_login_at',
		visibleTo: 'Nobody (no screen reads this column yet)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'last-login',
		dataItem: 'Last login',
		recordedWhen: 'Every successful login',
		storedIn: 'users.last_login_at, users.last_login_status',
		visibleTo: 'Nobody (no screen reads these columns yet)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'account-lock',
		dataItem: 'Account locked / unlocked',
		recordedWhen: 'An admin locks or unlocks a user account',
		storedIn: 'users.last_locked_at, users.last_unlocked_at',
		visibleTo: 'Nobody (no screen reads these columns yet)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'email-verification',
		dataItem: 'Email verification',
		recordedWhen: 'A user proves ownership of their email address',
		storedIn: 'users.email_verified_at, users.email_verified_source',
		visibleTo: 'Nobody (no screen reads these columns yet)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'admin-access-sessions',
		dataItem: 'Admin access sessions',
		recordedWhen: 'A Neoni admin opens or continues using an organisation',
		storedIn: "access_audit_events (event_type='access_session', access_kind='admin')",
		visibleTo: 'Nobody yet (an admin-only per-organisation API exists, no screen uses it)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'delegated-access-sessions',
		dataItem: 'Delegated access sessions',
		recordedWhen: 'A delegate from another organisation opens or continues using a grantor organisation',
		storedIn: "access_audit_events (event_type='access_session', access_kind='delegated')",
		visibleTo: "Nobody yet (the grantor organisation's own API exists, no screen uses it)",
		status: 'Collected, no screen yet',
	},
	{
		_id: 'access-denied',
		dataItem: 'Denied access attempts',
		recordedWhen: 'An admin or delegated access attempt is refused',
		storedIn: "access_audit_events (event_type='access_denied')",
		visibleTo: 'Nobody yet (no screen uses it)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'consent-changed',
		dataItem: 'Admin access consent changes',
		recordedWhen: "An organisation turns its 'Permit Neoni access' setting on or off",
		storedIn: "access_audit_events (event_type='consent_changed')",
		visibleTo: 'Nobody yet (no screen uses it)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'grant-lifecycle',
		dataItem: 'Delegated access grant lifecycle',
		recordedWhen: 'A delegated access grant is requested, approved, changed, revoked or rejected',
		storedIn:
			"access_audit_events (event_type in 'grant_requested', 'grant_approved', 'grant_level_changed', 'grant_revoked', 'grant_rejected')",
		visibleTo: 'Nobody yet (no screen uses it)',
		status: 'Collected, no screen yet',
	},
	{
		_id: 'invitation-lifecycle',
		dataItem: 'Invitation lifecycle',
		recordedWhen: 'An invitation is sent, expires, is completed (signed up or accepted) or is declined',
		storedIn:
			'admin_invitations / supply_chain_invitations (status, expires_at, completed_at)',
		visibleTo: 'The admin or organisation that sent the invitation, via the invitations list',
		status: 'Collected',
	},
	{
		_id: 'upgrade-requests',
		dataItem: 'Training account upgrade requests',
		recordedWhen: 'A training user requests to upgrade to a regular account',
		storedIn: 'users.requested_upgrade_at, users.requested_upgrade_message',
		visibleTo: 'Neoni admins, via the admin company page',
		status: 'Collected',
	},
	{
		_id: 'application-logs',
		dataItem: 'Application server logs',
		recordedWhen: 'Every request',
		storedIn: 'Heroku log drain',
		visibleTo: 'Neoni engineers only',
		status: 'Collected, IDs only, no personal data',
	},
]

function AdminSystemLogs() {
	return (
		<PageTemplate testid='admin-system-logs-main'>
			<Helmet>
				<title>{routes.adminSystemLogs.name}</title>
			</Helmet>
			<PageHeadings title='System Logs' />
			<div className='mt-4 text-sm'>
				This is a placeholder: a screen to refine which of this data is useful to show, and to whom.
			</div>
			<SimpleSortTable additionalClass='mt-4' data={rows} columns={columns} />
		</PageTemplate>
	)
}

export default AdminSystemLogs
